// @flow
/* eslint no-unreachable: 0 */

import { combineReducers } from 'redux';
import { normalize, schema } from 'normalizr';
import merge from 'lodash/merge';
import mapValues from 'lodash/mapValues';
import invert from 'lodash/invert';

import type { ThunkAction } from '../../types/redux';
import type { Category } from '../../types/model';

import { getCategory, getCategories } from '../../api';

export const RECEIVE_ALL_CATEGORIES_PENDING = 'RECEIVE_ALL_CATEGORIES_PENDING';
export const RECEIVE_ALL_CATEGORIES_FULFILLED = 'RECEIVE_ALL_CATEGORIES_FULFILLED';
export const RECEIVE_ALL_CATEGORIES_REJECTED = 'RECEIVE_ALL_CATEGORIES_REJECTED';

export const RECEIVE_CATEGORY_PENDING = 'RECEIVE_CATEGORY_PENDING';
export const RECEIVE_CATEGORY_FULFILLED = 'RECEIVE_CATEGORY_FULFILLED';
export const RECEIVE_CATEGORY_REJECTED = 'RECEIVE_CATEGORY_REJECTED';

const productSchema = new schema.Entity('products');
const articleSchema = new schema.Entity('articles');
const categorySchema = new schema.Entity('categories', {
  products: [productSchema],
  articles: [articleSchema],
});
const allCategoriesSchema = [categorySchema];

export type State = {
  allError: null | string,
  allLoading: boolean,
  all: Array<number>,
  byId: { [key: number]: Category },
  bySlug: { [key: string]: number }
}

export function requestAllCategories() : ThunkAction {
  return (dispatch, getState, { axios }) => {
    const { intl: { locale } } = getState();

    dispatch({ type: RECEIVE_ALL_CATEGORIES_PENDING });

    return getCategories(locale)
      .then(({ data }) => normalize(data, allCategoriesSchema))
      .then(normalizedData => {
        return dispatch({
          type: RECEIVE_ALL_CATEGORIES_FULFILLED,
          payload: normalizedData
        });
      }, error => {
        return dispatch({
          type: RECEIVE_ALL_CATEGORIES_REJECTED,
          payload: error.message
        })
      })
  };
}

export function requestCategory(id: number) : ThunkAction {
  return (dispatch, getState, { axios }) => {
    const { intl: { locale } } = getState();

    dispatch({ type: RECEIVE_CATEGORY_PENDING });

    return getCategory(id, locale)
      .then(({ data }) => normalize(data, categorySchema))
      .then(normalizedData => {
        return dispatch({
          type: RECEIVE_CATEGORY_FULFILLED,
          payload: normalizedData
        });
      }, error => {
        return dispatch({
          type: RECEIVE_CATEGORY_REJECTED,
          payload: error.message
        })
      })
  };
}

function allLoading(state = false, action) {
  switch (action.type) {
    case RECEIVE_ALL_CATEGORIES_PENDING:
      return true
    case RECEIVE_ALL_CATEGORIES_FULFILLED:
    case RECEIVE_ALL_CATEGORIES_REJECTED:
      return false;
    default:
      return state;
  }
}

function allError(state = null, action) {
  switch (action.type) {
    case RECEIVE_ALL_CATEGORIES_REJECTED:
      return action.payload;
    case RECEIVE_ALL_CATEGORIES_PENDING:
    case RECEIVE_ALL_CATEGORIES_FULFILLED:
      return null;
    default:
      return state;
  }
}

function all(state = [], action) {
  switch (action.type) {
    case RECEIVE_ALL_CATEGORIES_FULFILLED:
      return action.payload.result;
    default:
      return state;
  };
}

function decorateContentType(rawCategory: Object): Category {
  return ({
    ...rawCategory,
    contentType: rawCategory.contentType === 0 ? 'doctor' : 'patient',
  });
}

function byId(state = {}, action) {
  switch (action.type) {
    case RECEIVE_ALL_CATEGORIES_FULFILLED:
    case RECEIVE_CATEGORY_FULFILLED:
      return merge({}, state, mapValues(action.payload.entities.categories, decorateContentType));
    default:
      return state;
  };
}

function bySlug(state = {}, action) {
  switch (action.type) {
    case RECEIVE_ALL_CATEGORIES_FULFILLED:
    case RECEIVE_CATEGORY_FULFILLED:
      return merge({},
        state,
        mapValues(
          invert(
            mapValues(action.payload.entities.categories, cat => cat.slug)
          ),
          id => parseInt(id, 10)
        )
      );
    default:
      return state;
  };
}

export const selectors = {
  getAll: (state: State): Array<Category> => {
    return state.all
      .map(id => state.byId[id]);
  },
  getById: (state: State, id: number): ?Category => {
    return state.byId[id];
  },
  getBySlug: (state: State, slug: string): ?Category => {
    return state.byId[state.bySlug[slug]];
  },
  getByContentType: (state: State, contentType: string): Array<Category> => {
    return state.all
      .map(id => state.byId[id])
      .filter(cat => cat.contentType === contentType)
  }
}

export default combineReducers({
  allError,
  allLoading,
  all,
  byId,
  bySlug,
});



// WEBPACK FOOTER //
// ./src/redux/modules/categories.js